import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useState } from 'react';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

export type PlacementRenderProps = {
	onDismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'ai-powered-risk-assessment-spotlights',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: true, // For FedRAMP
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>((Messages) => ({ preloadedInitialData }) => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage>;
	}>('/', {
		autoRefetch: {
			isEnabled: !preloadedInitialData,
			updateKey: 'ai-powered-risk-assessment-spotlights',
		},
	});

	const messages = preloadedInitialData?.messages || data?.messages;

	const visibleMessages = messages
		?.filter((message) => !dismissedIds.has(message.messageInstanceId))
		?.slice?.(0, 1); // Only show one message at most

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedIds((prev) => new Set([...Array.from(prev), messageInstanceId]));
	};

	if (error) {
		throw error;
	}

	if (!preloadedInitialData && isLoading) {
		return null;
	}

	if (!visibleMessages || visibleMessages.length === 0) {
		return null;
	}

	return (
		<>
			{visibleMessages.map((message) => (
				<Messages.render
					key={message.messageInstanceId}
					onDismiss={() => handleDismiss(message.messageInstanceId)}
					{...message}
				/>
			))}
		</>
	);
});
